import { P1S } from '../settings/schema';
import type { PlacedObject } from '../store/types';

/** Rough volume of triangle mesh via divergence theorem (Y-up). */
export function meshVolumeMm3(positions: Float32Array, scale: [number, number, number]): number {
  let vol = 0;
  const [sx, sy, sz] = scale;
  for (let i = 0; i + 8 < positions.length; i += 9) {
    const ax = positions[i] * sx,
      ay = positions[i + 1] * sy,
      az = positions[i + 2] * sz;
    const bx = positions[i + 3] * sx,
      by = positions[i + 4] * sy,
      bz = positions[i + 5] * sz;
    const cx = positions[i + 6] * sx,
      cy = positions[i + 7] * sy,
      cz = positions[i + 8] * sz;
    vol += ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx);
  }
  return Math.abs(vol) / 6;
}

export interface Estimate {
  volumeCm3: number;
  filamentGrams: number;
  approxMinutes: number;
  note: string;
}

export function estimatePrint(
  objects: PlacedObject[],
  settings: Record<string, string | number | boolean>
): Estimate {
  let vol = 0;
  for (const o of objects) vol += meshVolumeMm3(o.positions, o.scale);
  const density =
    typeof settings.filament_density === 'number'
      ? settings.filament_density
      : parseFloat(String(settings.filament_density ?? 1.26));
  const infill =
    typeof settings.sparse_infill_density === 'number'
      ? settings.sparse_infill_density
      : parseFloat(String(settings.sparse_infill_density ?? 15));
  const walls = Number(settings.wall_loops ?? 2);
  // Heuristic fill factor: shells dominate small parts; infill for interior
  const fillFactor = Math.min(1, 0.25 + walls * 0.08 + (infill / 100) * 0.55);
  const volumeCm3 = (vol / 1000) * fillFactor;
  const filamentGrams = volumeCm3 * density;
  const layerH = Number(settings.layer_height ?? 0.2);
  const speed = Number(settings.inner_wall_speed ?? 300);
  // Very rough time: volume / (nozzle_area * speed) + layer overhead
  const nozzleArea = Math.PI * Math.pow(P1S.nozzle / 2, 2);
  const pathMm = (vol * fillFactor) / (nozzleArea * 0.9 * layerH);
  const approxMinutes = Math.max(1, Math.round(pathMm / speed / 60 + (vol / 1000) * 2));
  return {
    volumeCm3: Math.round(volumeCm3 * 100) / 100,
    filamentGrams: Math.round(filamentGrams * 10) / 10,
    approxMinutes,
    note: 'Rough estimate from mesh volume heuristics — not a true slice preview.',
  };
}
